import React, { useState } from "react";
import { Dialog, Typography } from "@mui/material";
import {
  VideoCameraIcon,
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useRequestsApi } from "../../../hooks/useRequestsApi";
import { toast } from "react-hot-toast";

interface CreateMeetingModalProps {
  open: boolean;
  onClose: () => void;
  requestId: string;
  onMeetingCreated: () => void;
}

const CreateMeetingModal: React.FC<CreateMeetingModalProps> = ({
  open,
  onClose,
  requestId,
  onMeetingCreated,
}) => {
  const [googleMeetLink, setGoogleMeetLink] = useState("");
  const [loading, setLoading] = useState(false);
  const { createMeeting } = useRequestsApi();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!googleMeetLink.trim()) {
      toast.error("Veuillez entrer un lien Google Meet valide");
      return;
    }

    try {
      setLoading(true);
      const response = await createMeeting(requestId, {
        link: googleMeetLink.trim(),
      });

      console.log("📨 Réponse createMeeting:", response);

      if (response.status === 201) {
        toast.success("Réunion créée avec succès !");
        onMeetingCreated();
        onClose();
        setGoogleMeetLink("");
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors de la création de la réunion";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Erreur createMeeting:", error);
      toast.error(error?.message || "Erreur lors de la création de la réunion");
    } finally {
      setLoading(false);
    }
  };

  const generateMeetLink = () => {
    // Générer un lien Meet basique (en production, vous pourriez utiliser l'API Google Meet)
    const randomId = Math.random().toString(36).substring(2, 15);
    setGoogleMeetLink(`https://meet.google.com/${randomId}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      <div className="bg-white rounded-xl shadow-xl flex flex-col max-h-[90vh] w-full">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b flex-shrink-0 bg-green-600 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-full">
                <VideoCameraIcon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold">
                  Créer une réunion
                </h2>
                <p className="text-green-100 text-sm sm:text-base">
                  Configurez le lien Google Meet pour cette séance
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-hidden">
          <div className="p-3 sm:p-6 overflow-y-auto max-h-96">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Lien Google Meet */}
              <div>
                <label
                  htmlFor="googleMeetLink"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Lien Google Meet *
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="url"
                    id="googleMeetLink"
                    value={googleMeetLink}
                    onChange={(e) => setGoogleMeetLink(e.target.value)}
                    placeholder="https://meet.google.com/abc-defg-hij"
                    className="flex-1 block w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={generateMeetLink}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm sm:text-base transition-colors flex items-center justify-center space-x-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>Générer</span>
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Le lien sera partagé avec l'élève une fois créé
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-6 border-t bg-gray-50 rounded-b-xl flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm sm:text-base"
          >
            Annuler
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !googleMeetLink.trim()}
            className="w-full sm:w-auto px-4 py-2 text-white bg-green-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex items-center justify-center hover:bg-green-700"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Création...
              </>
            ) : (
              <>
                <VideoCameraIcon className="w-4 h-4 mr-2" />
                Créer la réunion
              </>
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CreateMeetingModal;
